/**
 * Phase 5 PR 5.1 — `useActiveSeason` hook.
 *
 * Reads the user's active `seasons` row. Mirrors vanilla pattern from
 * `src/renderer/js/seasons/season-manager.js`:
 *
 *     SELECT * FROM seasons
 *     WHERE user_id = <me> AND is_active = true
 *     ORDER BY start_date DESC
 *     LIMIT 1
 *
 * Used by:
 *   - useTodaySession (needs season_id to scope training_plans query)
 *   - useWeeklyPlan   (same)
 *   - useUpcomingRace (needs team_id for OR-scoped query)
 *   - PR 5.2 useMacroPlan (seasonId is the URL/query parameter)
 *
 * Returns `{ data, loading, error, refetch }` — the canonical hook
 * shape established by `useAthleteContext`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { sharedRequest, dropSharedRequest } from '../lib/sharedRequest';

/**
 * ⚖️ B47 fix 8 (round-36 critic F2) — THE SEASON READ HAS AN END.
 *
 * `today.tsx` feeds this hook's `loading` into the retry surface's
 * `readsInFlight`, and that surface has exactly one control. A `seasons` query
 * that is accepted and never answered — the half-open Data API, the same
 * outage that opens the surface in the first place — left `loading` true for
 * ever, so after ONE press the button stayed disabled reading "Trying again…",
 * the beat never completed, and the escalation sentence was unreachable for
 * the rest of the session. Force-quitting the app was the only way out.
 *
 * The same 10 s as the athlete-context read, for the same reason: supabase-js
 * retries its own fetch out to about 9.4 s on a dead connection, and a
 * deadline inside that window would fail a merely slow network.
 *
 * A read the deadline gave up on is a FAILED read — `error` set, `loading`
 * false, `settled` true — the same state a throw already produces, so no
 * consumer meets a shape it has never seen. Two differences from the throw
 * path, both deliberate:
 *
 *   · `data` is LEFT AS IT WAS. The deadline gives up on an answer; it does
 *     not throw away one already in hand (fix 6's ratified rule). Wiping a
 *     season the athlete has would offer her "Start my season" on the next
 *     render, which is round-15 critic 5 by another road. ⚖️ B47 fix 9 (B172) —
 *     for THIS ACCOUNT only: a read for a different account drops `data` before
 *     it starts, so "a season in hand" can never mean the last reader's.
 *   · the request is ABANDONED, not aborted (fix 6's substitution, ratified by
 *     the coordinator): the answer is downloaded and dropped rather than
 *     cancelled, so no Supabase double in the repo has to grow a signal.
 */
const SEASON_READ_DEADLINE_MS = 10_000;

/** The race token for the deadline above. A symbol, so no row can be mistaken
 *  for it. */
const SEASON_READ_DEADLINE = Symbol('active-season-read-deadline');

export interface ActiveSeason {
  id: string;
  user_id: string;
  team_id: string | null;
  name: string;
  sport: string;
  start_date: string; // ISO date string ("YYYY-MM-DD")
  end_date: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface UseActiveSeasonResult {
  data: ActiveSeason | null;
  loading: boolean;
  /**
   * True only after a REAL query has completed (success or error) with a ready
   * client + user. Distinguishes "not ready yet" — the transient boot window
   * where `supabase`/`userId` are momentarily null and the hook reports
   * `{loading:false, data:null}` — from "fetched, genuinely no active season".
   *
   * Consumers that redirect on the absence of a season (e.g. plan-draft) MUST
   * gate on `settled`, not on `!loading`, or a transient null latches a bounce.
   *
   * ⚖️ B47 fix 8 — a read the deadline gave up on settles too. A hook that
   * never settles is a consumer waiting for ever, which is the defect this
   * lane removes and not a guard against it; `error` is what tells a consumer
   * the empty is UNKNOWN rather than real, and `data` still holds whatever
   * the last answered read put there.
   */
  settled: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const EMPTY_RESULT: UseActiveSeasonResult = {
  data: null,
  loading: false,
  settled: false,
  error: null,
  refetch: async () => {},
};

export function useActiveSeason(): UseActiveSeasonResult {
  const { supabase, user } = useAuthContext();
  const userId = user?.id ?? null;

  const [data, setData] = useState<ActiveSeason | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [settled, setSettled] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  /**
   * ⚖️ B47 fix 9 (B172, round-46 security F1 + critic S4) — WHICH READ MAY
   * WRITE. The deadline abandons a read without cancelling it, and this hook
   * had neither a sequence token nor an effect cleanup: an old read's deadline
   * fired over a newer read in flight and committed `loading:false,
   * settled:true` plus its `error` on top of it, and after an in-place account
   * switch the run belonging to the previous account went on writing. Every
   * state write below belongs to the read that is current when it happens.
   */
  const readSeq = useRef(0);
  /**
   * ⚖️ B47 fix 9 (B172) — WHOSE SEASON `data` IS, the `rawFor` shape from
   * `useAthleteContext`. Fix 8 decided that the deadline never throws away a
   * season already in hand; on an in-place switch that turned into the PREVIOUS
   * account's season rendered under the new session, settled, with `today.tsx`
   * naming it on the dashboard and the onboarding gate deciding on it. The
   * decision stands for the same account and ends at the account boundary.
   */
  const dataFor = useRef<string | null>(null);
  /**
   * B183 (2026-09-26) — has THIS instance already attempted a real query for
   * this uid (success, failure, OR still hanging — set the moment the query
   * is issued, not on settle). `sharedRequest` only joins an in-flight entry
   * for a uid this instance hasn't tried yet — the concurrent-mount case
   * (this hook direct + via `useTeamMeets`, both firing their FIRST attempt
   * in the same commit). An EXPLICIT retry for a uid already attempted
   * (`refetch()` again, e.g. the retry surface, while the FIRST read is
   * still genuinely hanging) always issues its own fresh query — the B47
   * fix 9 contract this hook already carries: a later call is never left
   * rejoining a promise that may never settle.
   */
  const queriedFor = useRef<string | null>(null);

  const refetch = useCallback(async () => {
    // The token advances on EVERY entry, the not-ready return included: a read
    // that is superseded before it starts is still superseded.
    const seq = (readSeq.current += 1);
    if (!supabase || !userId) {
      // Not ready — no client/user to query yet. This is NOT a settled empty:
      // leave `settled` false so consumers keep waiting instead of treating
      // the transient null as "no season" and redirecting.
      dataFor.current = null;
      setData(null);
      setLoading(false);
      setSettled(false);
      setError(null);
      return;
    }
    // A season in hand belongs to ONE account. Dropped before the new account's
    // read starts, exactly as `useAthleteContext` drops `raw`, so no settle path
    // — least of all the deadline, which leaves `data` as it found it — can
    // leave the previous account's season live under this session.
    if (dataFor.current !== userId) {
      dataFor.current = null;
      setData(null);
    }
    setLoading(true);
    setError(null);
    // ⚖️ B47 fix 8 — the deadline. Cleared in `finally`, so a read that answers
    // in time leaves no timer behind and behaves exactly as it did before.
    let deadline: ReturnType<typeof setTimeout> | undefined;
    const deadlinePassed = new Promise<typeof SEASON_READ_DEADLINE>((resolve) => {
      deadline = setTimeout(() => resolve(SEASON_READ_DEADLINE), SEASON_READ_DEADLINE_MS);
    });
    try {
      // B183 (2026-09-26) — `today.tsx` calls this hook directly AND via
      // `useTeamMeets` (`useTeamMeets().data?.team_id` reads
      // `useActiveSeason` itself), so a healthy Today render mounted TWO
      // instances of this hook and fired the `seasons` query twice. Keyed by
      // uid (B233's pattern) so two accounts never share a row; wrapped in
      // `Promise.resolve` BEFORE handing it to `sharedRequest` because a
      // Postgrest builder is a thenable whose OWN `.then()` re-fetches on
      // every call — sharing the bare builder across two `await`ers would
      // double the query instead of collapsing it (the same class as the
      // Capacitor-proxy thenable trap, just from the Postgrest client).
      const key = `seasons:${userId}`;
      const runQuery = () =>
        Promise.resolve(
          supabase
            .from('seasons')
            .select('*')
            .eq('user_id', userId)
            .eq('is_active', true)
            .order('start_date', { ascending: false })
            .limit(1)
            .maybeSingle(),
        );
      // Only THIS instance's first-ever attempt for this uid may join an
      // in-flight sibling; a later retry for a uid already attempted always
      // gets its own fresh query (see `queriedFor` above).
      const isFirstAttemptForUid = queriedFor.current !== userId;
      queriedFor.current = userId;
      const pending = isFirstAttemptForUid ? sharedRequest(key, runQuery) : runQuery();
      const settledRead = await Promise.race([pending, deadlinePassed]);
      // ⚖️ B47 fix 9 — a newer read owns this state now: an older read's
      // deadline may not report a failure over a read that is still alive, and
      // an older read's row may not answer for the account that replaced it.
      if (seq !== readSeq.current) return;
      if (settledRead === SEASON_READ_DEADLINE) {
        // ⚖️ B47 fix 8 — an abandoned read is a FAILED read, never a silent
        // success and never a settled "no season". `error` says the answer is
        // unknown, `finally` below ends `loading` so the one control on the
        // retry surface comes back, and `data` stays exactly as it was — for
        // this account, which is what the stamp above now guarantees.
        //
        // B183 — AND THIS READ STOPS ANSWERING FOR THE NEXT MOUNT (the B242
        // pattern `useUserTier.ts` already carries): drop the shared entry
        // only if it is still THIS read, so the next mount issues a fresh
        // query instead of rejoining a dead one.
        dropSharedRequest(key, pending);
        setError(new Error('season read timed out'));
        return;
      }
      const { data: rows, error: queryError } = settledRead;
      if (queryError) throw queryError;
      dataFor.current = userId;
      setData((rows as ActiveSeason | null) ?? null);
      // ⚖️ B47 fix 9 (round-46 critic S4) — AND A SUCCESS CLEARS THE ERROR.
      // `error` was cleared only at the START of a read, which every success
      // does follow — so with the sequence token above this line is a BELT, not
      // a fix, and no case can turn it red on its own. It is here because the
      // contract `today.tsx:923` relies on ("a settled success never carries an
      // error") should be true on the success path itself rather than by a
      // distant `setError(null)` and a token argument.
      setError(null);
    } catch (err) {
      if (seq !== readSeq.current) return;
      dataFor.current = null;
      setData(null);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (deadline !== undefined) clearTimeout(deadline);
      // A real query ran to completion (or errored) — the answer is now KNOWN.
      if (seq === readSeq.current) {
        setLoading(false);
        setSettled(true);
      }
    }
  }, [supabase, userId]);

  useEffect(() => {
    void refetch();
    // ⚖️ B47 fix 9 (B172) — the run that is being replaced stops speaking. A
    // uid change, or an unmount, leaves a read in flight with no way to know
    // it; the token is what tells it.
    //
    // ⚠️ A BELT with no case of its own: on a uid change `refetch` advances the
    // token in the same flush anyway, and React 18 does not warn about a
    // setState after unmount, so nothing observable changes without this line.
    // It is here so that "an abandoned read can write" is false by construction
    // rather than by the ordering of two effects.
    return () => {
      readSeq.current += 1;
    };
  }, [refetch]);

  if (!supabase || !userId) {
    return EMPTY_RESULT;
  }

  return { data, loading, settled, error, refetch };
}
